import { useState, useCallback, useRef, useEffect } from 'react';
import useSettings from './useSettings';

// Cache global para evitar múltiplas requisições
const settingsCache = {
  data: null,
  timestamp: null,
  loading: false,
  listeners: new Set()
};

// Cache duration: 5 minutos
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Hook que fornece configurações com cache para evitar múltiplas requisições
 * Implementa pattern singleton + cache com listeners para updates
 */
const useCachedSettings = () => {
  const { getAll: getOriginalSettings } = useSettings();
  const [settings, setSettings] = useState(settingsCache.data || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const listenerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Registrar listener para updates
  const registerListener = useCallback((callback) => {
    if (listenerRef.current) {
      settingsCache.listeners.delete(listenerRef.current);
    }
    const safeCallback = (newSettings) => {
      if (isMountedRef.current) {
        callback(newSettings);
      }
    };
    listenerRef.current = safeCallback;
    settingsCache.listeners.add(safeCallback);

    // Cleanup: remover listener quando componente desmontar
    return () => {
      if (listenerRef.current) {
        settingsCache.listeners.delete(listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, []);

  // Notificar todos os listeners sobre mudanças
  const notifyListeners = useCallback((newSettings) => {
    settingsCache.listeners.forEach(listener => {
      try {
        listener(newSettings);
      } catch (error) {
        console.warn('Erro ao notificar listener de settings:', error);
      }
    });
  }, []);

  // Verificar se cache é válido
  const isCacheValid = useCallback(() => {
    if (!settingsCache.data || !settingsCache.timestamp) return false;
    return (Date.now() - settingsCache.timestamp) < CACHE_DURATION;
  }, []);

  // Carregar settings com cache
  const loadSettings = useCallback(async (forceRefresh = false) => {
    // Se já está carregando, aguardar
    if (settingsCache.loading && !forceRefresh) {
      return settingsCache.data;
    }

    // Se cache é válido e não é refresh forçado, retornar cache
    if (isCacheValid() && !forceRefresh) {
      if (isMountedRef.current) {
        setSettings(settingsCache.data);
      }
      return settingsCache.data;
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }
      settingsCache.loading = true;

      const settingsData = await getOriginalSettings();
      
      // Verificar se componente ainda está montado antes de atualizar estado
      if (!isMountedRef.current) {
        settingsCache.loading = false;
        return settingsCache.data;
      }
      
      // Converter array para objeto para facilitar acesso
      const settingsMap = {};
      if (Array.isArray(settingsData)) {
        settingsData.forEach(setting => {
          settingsMap[setting.key] = setting.value;
        });
      }

      // Atualizar cache global
      settingsCache.data = settingsMap;
      settingsCache.timestamp = Date.now();
      settingsCache.loading = false;

      // Atualizar estado local apenas se componente ainda está montado
      if (isMountedRef.current) {
        setSettings(settingsMap);
      }

      // Notificar todos os listeners
      notifyListeners(settingsMap);

      return settingsMap;

    } catch (error) {
      console.error('❌ Erro ao carregar configurações:', error);
      if (isMountedRef.current) {
        setError(error.message || 'Erro ao carregar configurações');
      }
      settingsCache.loading = false;
      throw error;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [getOriginalSettings, isCacheValid, notifyListeners]);

  // Invalidar cache
  const invalidateCache = useCallback(() => {
    console.log('🗑️ Invalidando cache de configurações');
    settingsCache.data = null;
    settingsCache.timestamp = null;
    settingsCache.loading = false;
  }, []);

  // Obter valor específico de configuração
  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] || defaultValue;
  }, [settings]);

  // Atualizar configuração específica no cache
  const updateCachedSetting = useCallback((key, value) => {
    if (settingsCache.data) {
      settingsCache.data[key] = value;
      const updatedSettings = { ...settingsCache.data };
      setSettings(updatedSettings);
      notifyListeners(updatedSettings);
    }
  }, [notifyListeners]);

  return {
    settings,
    loading,
    error,
    loadSettings,
    invalidateCache,
    getSetting,
    updateCachedSetting,
    registerListener,
    isCacheValid: isCacheValid()
  };
};

export default useCachedSettings;